import resultsReviewDispatcher from '../../action';
import {
  HS_RESULTS_REVIEW_DISPLAY_STATUS,
  HS_RESULTS_REVIEW_STATUS_COLORS,
} from '../../constants';
import { NewCalendar, ReviewIcon } from '@/assets/svg';
import { urlLabel } from '@/enum/PermissionEnum';
import CustomButton from '@/new-components/CustomButton';
import customToast from '@/new-components/CustomNotification';
import { CustomStatusComponent } from '@/new-components/CustomStatusComponent';
import CustomTabs from '@/new-components/CustomTabs';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Divider,
  Container,
  withStyles,
  InputLabel,
  InputAdornment,
  Fab,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: 'calc(100vh - 222px)',
    overflowY: 'hidden',
    overflowX: 'scroll',
  },
  title: {
    width: '90%',
    fontWeight: 600,
    marginRight: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const CustomInputLabel = withStyles((theme) => ({
  root: {
    color: '#333333',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))(InputLabel);

const CustomFab = withStyles({
  root: {
    width: 40,
    height: 40,
    backgroundColor: 'white',
    borderRadius: 12,
    boxShadow: 'none',
  },
})(Fab);

const CustomToggleButtonGroup = withStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  grouped: {
    margin: theme.spacing(0.5),
    border: 'none',
    '&:not(:first-child)': {
      borderLeft: '1px solid #E1E1E1',
      borderRadius: 8,
    },
    '&:first-child': {
      borderRadius: 8,
    },
  },
}))(ToggleButtonGroup);

const CustomToggleButton = withStyles((theme) => ({
  root: {
    flex: 1,
    border: '1px solid #E1E1E1',
    color: '#999999',
    textTransform: 'unset',
    fontSize: 14,
    height: 36,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.primary.light,
    },
    '&.error': {
      border: `1px solid ${theme.palette.feedback.error}`,
    },
  },
}))(ToggleButton);

const CustomTextField = withStyles({
  root: {
    fontSize: 14,
    width: '100%',
    lineHeight: '20px',

    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: (props) => (props.hasClear ? 0 : 30),
      padding: '8px 5px',
      fontSize: 16,
    },

    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& .MuiInputBase-multiline': {
      height: 'auto',
      padding: 8,
      paddingLeft: 0,
      fontSize: 14,
      lineHeight: '20px',

      '& .MuiOutlinedInput-input': {
        marginRight: 8,
      },
    },
  },
})(TextField);

const ReviewScreen = ({
  paxScreeningId,
  reviewData,
  onSaveDraft,
  onSubmitAll,
  onSubmitReview,
  setIsSubmitAll,
  isAdmin,
}) => {
  // Create tabs from testReviewItems
  const tabs =
    reviewData?.testReviewItems?.map((item, index) => ({
      label: item.testName,
      id: item.id,
      data: item,
      disabled: !item.link || Boolean(item.isOptOut),
      chipCustomSurvey: item.reviewStatus === 'Completed' ? 1 : 0,
    })) || [];

  // Find the first tab with documentFilePath
  const initialSelectedTab = useMemo(() => {
    const idx = tabs.findIndex(
      (tab) => !tab.disabled && tab.data && tab.data.documentFilePath
    );
    if (idx !== -1) return idx;

    const firstEnabledIdx = tabs.findIndex((tab) => !tab.disabled);
    return firstEnabledIdx !== -1 ? firstEnabledIdx : 0;
  }, [tabs]);

  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form values for all test items
  const [formValues, setFormValues] = useState({});
  const [defaultData, setDefaultData] = useState({});

  const classes = useStyles();
  const history = useHistory();

  // Find the last enabled tab index
  const lastEnabledTabIndex =
    tabs.length > 0
      ? tabs.length - 1 - [...tabs].reverse().findIndex((tab) => !tab.disabled)
      : -1;
  const isLastTab = selectedTab === lastEnabledTabIndex;

  // Find the first enabled tab index
  const firstEnabledTabIndex = tabs.findIndex((tab) => !tab.disabled);
  const isFirstTab = selectedTab === firstEnabledTabIndex;

  // Initialize form values when reviewData changes
  useEffect(() => {
    if (reviewData?.testReviewItems) {
      const initialFormValues = {};
      reviewData.testReviewItems.forEach((item) => {
        initialFormValues[item.id] = {
          evaluation: item.screeningResult || '',
          comment: item.comment || '',
          reviewStatus: item.reviewStatus || '',
        };
      });
      setFormValues(initialFormValues);
      setDefaultData(initialFormValues);
    }
  }, [reviewData]);

  const handleNext = () => {
    if (!isLastTab) {
      setIsLoading(true);

      // Find the next enabled tab
      let nextTabIndex = selectedTab + 1;
      while (nextTabIndex < tabs.length && tabs[nextTabIndex].disabled) {
        nextTabIndex++;
      }

      // If we found an enabled tab, navigate to it
      if (nextTabIndex < tabs.length) {
        setSelectedTab(nextTabIndex);
        // Add a small delay to prevent iframe flickering
        setTimeout(() => setIsLoading(false), 100);
      } else {
        // If no more enabled tabs, submit
        handleSubmit();
      }
    } else {
      handleSubmit();
    }
  };
  const handleBackOrCancel = () => {
    if (isFirstTab) {
      history.goBack();
    } else {
      setIsLoading(true);

      // Find the previous enabled tab
      let prevTabIndex = selectedTab - 1;
      while (prevTabIndex >= 0 && tabs[prevTabIndex].disabled) {
        prevTabIndex--;
      }

      // If we found an enabled tab, navigate to it
      if (prevTabIndex >= 0) {
        setSelectedTab(prevTabIndex);
        // Add a small delay to prevent iframe flickering
        setTimeout(() => setIsLoading(false), 100);
      } else {
        // If no previous enabled tabs, go back
        history.goBack();
      }
    }
  };

  const handleSubmit = (submitAll = false) => {
    // Format form values into the expected parameter structure
    const reviewItems = Object.keys(formValues).map((testItemId) => {
      const formValue = formValues[testItemId];
      return {
        futReportReviewId: testItemId,
        screeningResult: formValue.evaluation,
        reviewStatus: formValue.reviewStatus,
        comment: formValue.comment || '',
      };
    });

    let params = {
      submitAll: submitAll,
      reviewItems: reviewItems,
    };

    onSubmitAll(params);
  };

  const handleOpenReviewPDFWithFooter = async () => {
    if (paxScreeningId && tabs[selectedTab]) {
      const currentTestItem = tabs[selectedTab].data;
      const blob = new Blob([currentTestItem.link], {
        type: 'application/pdf',
      });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    }
  };

  // Get current test item data
  const currentTestItem = tabs[selectedTab]?.data;
  const currentFormValue = currentTestItem
    ? formValues[currentTestItem.id]
    : { evaluation: '', comment: '' };

  const blobUrl = useMemo(() => {
    if (!currentTestItem?.link) return '';
    const link = currentTestItem?.link;

    const blob = new Blob([link], { type: 'application/pdf' });

    return URL.createObjectURL(blob);
  }, [currentTestItem]);

  const updateFormValue = (field, value) => {
    if (currentTestItem) {
      setFormValues((prev) => ({
        ...prev,
        [currentTestItem.id]: {
          ...prev[currentTestItem.id],
          [field]: value,
        },
      }));
    }
  };

  const handleSaveDraft = () => {
    onSaveDraft({
      ...formValues[currentTestItem.id],
      id: currentTestItem.id,
      reviewStatus: currentTestItem.reviewStatus,
    });
  };

  const handleSubmitReview = () => {
    onSubmitReview({
      ...formValues[currentTestItem.id],
      id: currentTestItem.id,
    });
  };

  const goBack = () => {
    history.push(`/${urlLabel.resultsReviewModule}`);
  };

  // Calculate outside the IIFE to avoid redeclaration errors

  const lastIndexTabCompleted = tabs.findLastIndex(
    (tab) => tab.data?.reviewStatus === 'Completed'
  );

  const lastIndexTabNotCompleted = tabs.findLastIndex(
    (tab) => tab.data?.reviewStatus !== 'Completed'
  );

  const enabledTestIds = useMemo(
    () => tabs.filter((tab) => !tab.disabled).map((tab) => tab.id),
    [tabs]
  );
  const isAnyItemIncomplete = useMemo(
    () =>
      enabledTestIds.some((id) => {
        if (formValues[id]?.evaluation === 'Normal') {
          return isEmpty(formValues[id]?.evaluation);
        }
        return (
          isEmpty(formValues[id]?.comment) ||
          isEmpty(formValues[id]?.evaluation)
        );
      }),
    [enabledTestIds, formValues]
  );

  const isRemindSave = useMemo(() => {
    let isValid = false;
    Object.keys(formValues).forEach((key) => {
      const filterFormValue = formValues[key];
      const filterDefaultData = defaultData[key];

      if (
        filterFormValue?.comment !== filterDefaultData?.comment ||
        filterFormValue?.evaluation !== filterDefaultData?.evaluation
      ) {
        isValid = true;
      }
    });
    resultsReviewDispatcher.setState('isRemindSave', isValid);
    return isValid;
  }, [defaultData, formValues]);

  const isDisabledSubmit = useMemo(() => {
    // If evaluation is Normal, comment is optional
    if (currentFormValue?.evaluation === 'Normal') {
      return isEmpty(currentFormValue?.evaluation);
    }

    return (
      isEmpty(currentFormValue?.comment) ||
      isEmpty(currentFormValue?.evaluation)
    );
  }, [currentFormValue]);

  return (
    <>
      <Container
        maxWidth={false}
        component={Paper}
        square
        variant="outlined"
        className={classes.container}
        style={{ padding: 0 }}
      >
        <Paper
          elevation={0}
          style={{
            minWidth: 220,
            borderRadius: 0,
            borderRight: '1px solid #f1f1f1',
            overflow: 'auto',
          }}
        >
          <CustomTabs
            listTab={tabs}
            value={selectedTab}
            onChange={(_, v) => {
              if (isRemindSave) {
                customToast(
                  'error',
                  'Please remember to save your changes, otherwise your input will not be saved.'
                );
              } else {
                setIsLoading(true);
                setSelectedTab(v);
                setTimeout(() => setIsLoading(false), 100);
              }
            }}
            orientation="vertical"
            renderLabel="label"
            renderKey="id"
            btnWidth={180}
          />
        </Paper>

        <Box
          flex={2}
          display="flex"
          flexDirection="column"
          bgcolor="#E1E1E1"
          position="relative"
        >
          <Box
            minHeight={56}
            bgcolor="#F6F6F6"
            py={1}
            px={2.5}
            display="flex"
            alignItems="center"
            borderBottom="1px solid #E1E1E1"
            justifyContent="space-between"
          >
            <Typography variant="h6" className={classes.title}>
              {tabs[selectedTab]?.label || 'No Test Selected'}
            </Typography>

            <CustomFab onClick={handleOpenReviewPDFWithFooter}>
              <ReviewIcon />
            </CustomFab>
          </Box>

          <Box
            flex={1}
            overflow="auto"
            py={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            position="relative"
          >
            {blobUrl && (
              <Box
                style={{
                  border: 'none',
                  minWidth: '90%',
                  minHeight: '100%',
                  position: 'relative',
                  opacity: isLoading ? 0.7 : 1,
                  transition: 'opacity 0.1s ease-in-out',
                }}
              >
                <iframe
                  key={currentTestItem?.id} // Force re-render when test item changes
                  src={`${blobUrl}#toolbar=0`}
                  style={{
                    border: 'none',
                    width: '100%',
                    height: '100%',
                    minHeight: '600px',
                  }}
                  title="PDF Preview"
                  frameBorder="0"
                  onLoad={() => setIsLoading(false)}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box
          flex={1}
          bgcolor="#fff"
          p={2.5}
          display="flex"
          flexDirection="column"
          borderLeft="1px solid #f1f1f1"
          overflow="auto"
          minWidth={420}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Typography
              variant="h6"
              style={{ fontWeight: 600, marginRight: 8 }}
            >
              Review follow-up test
            </Typography>

            <CustomStatusComponent
              filterList={HS_RESULTS_REVIEW_DISPLAY_STATUS}
              filterListColor={HS_RESULTS_REVIEW_STATUS_COLORS}
              status={tabs[selectedTab]?.data?.reviewStatus}
            />
          </Box>
          <Divider light style={{ marginBottom: 24 }} />

          <CustomInputLabel>Evaluation</CustomInputLabel>
          <CustomToggleButtonGroup
            exclusive
            value={currentFormValue?.evaluation}
            onChange={(e, newValue) => updateFormValue('evaluation', newValue)}
            disabled={currentTestItem?.reviewStatus === 'Completed' || isAdmin}
          >
            <CustomToggleButton
              value="Normal"
              disabled={
                currentTestItem?.reviewStatus === 'Completed' || isAdmin
              }
            >
              Normal
            </CustomToggleButton>
            <CustomToggleButton
              value="Abnormal"
              disabled={
                currentTestItem?.reviewStatus === 'Completed' || isAdmin
              }
            >
              Abnormal
            </CustomToggleButton>
            <CustomToggleButton
              value="Urgent"
              disabled={
                currentTestItem?.reviewStatus === 'Completed' || isAdmin
              }
            >
              Urgent
            </CustomToggleButton>
          </CustomToggleButtonGroup>

          <CustomInputLabel style={{ marginTop: 24 }}>Comment</CustomInputLabel>
          <CustomTextField
            multiline
            disabled={currentTestItem?.reviewStatus === 'Completed' || isAdmin}
            rows={5}
            value={currentFormValue?.comment}
            variant="outlined"
            placeholder="Enter details"
            onChange={(e) => updateFormValue('comment', e.target.value)}
          />
          {/* {currentTestItem?.reviewStatus !== 'Completed' && (
            <Box width="100%" display="flex" justifyContent="flex-end" mt={2}>
              <CustomButton
                variant="outlined"
                color="primary"
                disabled={
                  isEmpty(currentFormValue?.comment) ||
                  isEmpty(currentFormValue?.evaluation)
                }
                onClick={handleSaveDraft}
              >
                {currentTestItem?.reviewStatus === 'Pending' ||
                currentTestItem?.reviewStatus === 'ReviewRequired'
                  ? 'Update'
                  : 'Save as Draft'}
              </CustomButton>
            </Box>
          )} */}

          <CustomInputLabel style={{ marginTop: 24 }}>
            Saved by
          </CustomInputLabel>
          <CustomTextField
            disabled
            value={currentTestItem?.savedByDoctorName || 'Not signed'}
            variant="outlined"
            fullWidth
          />

          <CustomInputLabel style={{ marginTop: 24 }}>
            Saved on
          </CustomInputLabel>
          <CustomTextField
            value={
              currentTestItem?.savedDate
                ? moment(currentTestItem.savedDate).utc().format('DD/MM/YYYY')
                : 'Not signed'
            }
            disabled
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <NewCalendar />
                </InputAdornment>
              ),
            }}
          />

          <Divider light style={{ marginTop: 24 }} />

          <CustomInputLabel style={{ marginTop: 24 }}>
            Signed by
          </CustomInputLabel>
          <CustomTextField
            disabled
            value={currentTestItem?.signedByDoctorName || 'Not signed'}
            variant="outlined"
            fullWidth
          />

          <CustomInputLabel style={{ marginTop: 24 }}>
            Signed on
          </CustomInputLabel>
          <CustomTextField
            value={
              currentTestItem?.signedDate
                ? moment(currentTestItem.signedDate).utc().format('DD/MM/YYYY')
                : 'Not signed'
            }
            disabled
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <NewCalendar />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Container>
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        height={74}
        style={{ borderTop: '2px solid #e1e1e1', backgroundColor: '#fff' }}
      >
        <Box
          width="100%"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gridGap={16}
          px={2}
          height="100%"
        >
          {(() => {
            const renderCancelOrBack = () => {
              if (isFirstTab) {
                return (
                  <CustomButton
                    variant="outlined"
                    color="primary"
                    onClick={goBack}
                  >
                    Cancel
                  </CustomButton>
                );
              } else {
                return (
                  <CustomButton
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (isRemindSave) {
                        customToast(
                          'error',
                          'Please remember to save your changes, otherwise your input will not be saved.'
                        );
                      } else {
                        handleBackOrCancel();
                      }
                    }}
                  >
                    Back
                  </CustomButton>
                );
              }
            };

            // Check if there's only one document
            const isSingleDocument = tabs.length === 1;

            // Get current test item's review status
            const currentReviewStatus = currentTestItem?.reviewStatus;

            // 1. Draft, Pending, ReviewRequired
            if (
              !isAdmin &&
              ['Draft', 'Pending', 'ReviewRequired'].includes(
                currentReviewStatus
              )
            ) {
              if (isSingleDocument) {
                // Single document: Back, Save, Submit
                return (
                  <>
                    {renderCancelOrBack()}
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={handleSaveDraft}
                      disabled={isDisabledSubmit}
                    >
                      Save
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsSubmitAll(false);
                        handleSubmitReview();
                      }}
                      disabled={isDisabledSubmit}
                    >
                      Submit
                    </CustomButton>
                  </>
                );
              } else if (isLastTab) {
                // Last document: Back, Save, Submit, Submit All
                return (
                  <>
                    {renderCancelOrBack()}
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={handleSaveDraft}
                      disabled={isDisabledSubmit}
                    >
                      Save
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsSubmitAll(false);
                        handleSubmitReview();
                      }}
                      disabled={isDisabledSubmit}
                    >
                      Submit
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsSubmitAll(true);
                        handleSubmit(true);
                      }}
                      disabled={isAnyItemIncomplete}
                      width="170px"
                    >
                      Submit All
                    </CustomButton>
                  </>
                );
              } else {
                if (
                  lastIndexTabCompleted === lastEnabledTabIndex &&
                  lastIndexTabNotCompleted === selectedTab
                ) {
                  return (
                    <>
                      {renderCancelOrBack()}
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={handleSaveDraft}
                        disabled={isDisabledSubmit}
                      >
                        Save
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setIsSubmitAll(false);
                          handleSubmitReview();
                        }}
                        disabled={isDisabledSubmit}
                      >
                        Submit
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setIsSubmitAll(true);
                          handleSubmit(true);
                        }}
                        disabled={isAnyItemIncomplete}
                        width="170px"
                      >
                        Submit All
                      </CustomButton>
                    </>
                  );
                }

                // Middle document: Back, Save, Submit
                return (
                  <>
                    {renderCancelOrBack()}
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={handleSaveDraft}
                      disabled={isDisabledSubmit}
                    >
                      Save
                    </CustomButton>
                    <CustomButton
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsSubmitAll(false);
                        handleSubmitReview();
                      }}
                      disabled={isDisabledSubmit}
                    >
                      Submit
                    </CustomButton>
                  </>
                );
              }
            }
            if (
              isLastTab &&
              isAdmin &&
              ['Draft', 'Pending', 'ReviewRequired'].includes(
                currentReviewStatus
              )
            ) {
              return (
                <CustomButton
                  variant="outlined"
                  color="primary"
                  onClick={goBack}
                >
                  Back to Main
                </CustomButton>
              );
            }

            // 2. Completed
            if (currentReviewStatus === 'Completed') {
              if (isSingleDocument) {
                // Single document: Back to Main
                return (
                  <CustomButton
                    variant="outlined"
                    color="primary"
                    onClick={goBack}
                  >
                    Back to Main
                  </CustomButton>
                );
              } else if (isLastTab) {
                // Last document
                return (
                  <CustomButton
                    variant="outlined"
                    color="primary"
                    onClick={goBack}
                  >
                    Back to Main
                  </CustomButton>
                );
              } else {
                // Middle document: Back, Next
                // Explicitly check if this is a middle document
                const isMiddleDocument =
                  selectedTab > 0 && selectedTab < tabs.length - 1;
                if (isMiddleDocument) {
                  return (
                    <>
                      {renderCancelOrBack()}
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        Next
                      </CustomButton>
                    </>
                  );
                } else {
                  // Fallback for edge cases
                  return (
                    <>
                      {renderCancelOrBack()}
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        Next
                      </CustomButton>
                    </>
                  );
                }
              }
            }

            // Default fallback
            return (
              <>
                {renderCancelOrBack()}
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </CustomButton>
              </>
            );
          })()}
        </Box>
      </Box>
    </>
  );
};

export default ReviewScreen;
